import { useCallback, useEffect, useRef, useState } from 'react'
import { Spinner } from '.'
import { createClient } from '../util/supabase/component'
import type { SupabaseTables } from '../types'

const StatisticsCard = ({ title, value }: { title: string; value: number }) => (
  <div className="rounded-md border border-gray-300 p-4">
    <span className="block font-bold text-serblue-dark">{title}</span>
    {value.toLocaleString()} Records
  </div>
)

export const Statistics = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [speciesCount, setSpeciesCount] = useState<number>(0)
  const [allCount, setAllCount] = useState<number>(0)
  const [counts, setCounts] = useState<Partial<Record<SupabaseTables, number>>>(
    {},
  )
  const isFirstFetch = useRef(true)

  // Move supabase client creation outside of component body
  // to prevent recreation on each render
  const supabase = useCallback(createClient, [])()

  useEffect(() => {
    if (!isFirstFetch.current) return
    isFirstFetch.current = false
    const getRecordCount = (table: SupabaseTables) =>
      supabase
        .from(table)
        .select('id', { count: 'exact', head: true })
        .then(({ count }) => count ?? 0)

    const fetchData = async () => {
      try {
        const [
          speciesCount,
          oilContentCount,
          dispersalCount,
          proteinContentCount,
          seedWeightsCount,
          morphologyCount,
          storageBehaviourCount,
          germinationCount,
        ] = await Promise.all([
          getRecordCount('species'),
          getRecordCount('oil_content'),
          getRecordCount('dispersal'),
          getRecordCount('protein_content'),
          getRecordCount('seed_weights'),
          getRecordCount('morphology'),
          getRecordCount('storage_behaviour'),
          getRecordCount('germination'),
        ])

        setIsLoading(false)
        setSpeciesCount(speciesCount)
        setAllCount(
          oilContentCount +
            dispersalCount +
            proteinContentCount +
            seedWeightsCount +
            morphologyCount +
            storageBehaviourCount +
            germinationCount,
        )
        setCounts({
          oil_content: oilContentCount,
          dispersal: dispersalCount,
          protein_content: proteinContentCount,
          seed_weights: seedWeightsCount,
          morphology: morphologyCount,
          storage_behaviour: storageBehaviourCount,
          germination: germinationCount,
        })
      } catch (error) {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [supabase])

  return (
    <>
      <h3 className="mb-2 text-lg font-bold text-gray-800">Statistics</h3>
      {isLoading && <Spinner />}
      {!isLoading && (
        <div>
          <p>
            Currently, the database hosts {allCount.toLocaleString()} records
            from {speciesCount.toLocaleString()} taxa.
          </p>
          <div className="mt-5 grid w-full grid-cols-8 gap-2">
            {counts?.oil_content != null && (
              <StatisticsCard title="Oil Content" value={counts.oil_content} />
            )}
            {counts?.dispersal != null && (
              <StatisticsCard
                title="Dispersal agents"
                value={counts.dispersal}
              />
            )}
            {counts?.protein_content != null && (
              <StatisticsCard
                title="Protein Content"
                value={counts.protein_content}
              />
            )}
            {counts?.seed_weights != null && (
              <StatisticsCard
                title="Seed Weights"
                value={counts.seed_weights}
              />
            )}
            {counts?.morphology != null && (
              <StatisticsCard title="Morphology" value={counts.morphology} />
            )}
            {counts?.storage_behaviour != null && (
              <StatisticsCard
                title="Storage Behaviour"
                value={counts.storage_behaviour}
              />
            )}
            {counts?.germination != null && (
              <StatisticsCard title="Germination" value={counts.germination} />
            )}
          </div>
        </div>
      )}
    </>
  )
}
